import { Button, Card, CardContent, Grid, makeStyles, useTheme } from '@material-ui/core';
import { CheckCircle, ExpandLessOutlined, ExpandMoreOutlined, WarningOutlined } from '@material-ui/icons';
import React, { FC, ReactNode, useContext, useEffect, useState } from 'react';
import { useGlobalStyles } from '../../Home';
import HelperMenu from './HelperMenu';
import { useLocation } from 'react-router-dom';
import { TeamContext } from './../apps/edp/team/contexts/TeamProvider';
import { ComponentLocation, emptyComponentLocation } from '../../interfaces/ComponentLocation';

const useStyles = makeStyles((theme) => ({
    title: {
        fontSize: '20px',
        fontFamily: 'Gellix',
        fontWeight: 600,
        color: '#444',
        lineHeight: '32px',
    },
    subtitle: {
        margin: '25px 25px 10px 25px',
        fontSize: '16px',
        fontFamily: 'Gellix',
        textAlign: 'justify',
        lineHeight: '26px',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: '0 -8px',
        background: 'linear-gradient(270deg, rgba(0,41,82,1) 0%, rgba(34,183,197,1) 30%, rgba(245,248,249,1) 90%)',
    },
    titleContainer: {
        margin: 'auto 8px',
    },
    icon: {
        fontSize: '32px',
        margin: 'auto 8px',
    },
    toggle: {
        margin: 'auto 8px',
        flexShrink: 0,
    },
    helpLink: {
        margin: 'auto 8px',
    },
    toggleButton: {
        minWidth: 'unset',
        padding: '6px',
    },
    status: {
        marginRight: '5px',
    },
}));

interface SectionProps {
    icon: FC<{ className: string }>;
    enableHelp: boolean;
    title: string;
    titleKey: string;
    customSummary?: ReactNode;
    children: ReactNode;
    isCompleted?: boolean;
    autoClose?: boolean;
    onToggle?: (opened: boolean) => void;
    isStatic?: boolean;
    [x: string]: any;
    description?: string;
    isDescriptionHtml?: boolean;
}

const Section = ({
    icon,
    enableHelp,
    title,
    titleKey,
    customSummary,
    children,
    isCompleted,
    onToggle,
    isStatic,
    autoClose,
    description = undefined,
    isDescriptionHtml = false,
    ...otherProps
}: SectionProps) => {
    const location = useLocation();
    const theme = useTheme();
    const classes = { ...useStyles(), ...useGlobalStyles() };
    const [opened, setOpened] = useState<boolean>(true);
    const [sectionLocation, setSectionLocation] = useState<ComponentLocation>(emptyComponentLocation);
    const team = useContext(TeamContext);
    const Icon = icon;

    // Auto-close
    useEffect(() => {
        if (autoClose && isCompleted && opened) {
            setOpened(false);
            if (onToggle) onToggle(false);
        }
    }, [autoClose, isCompleted, opened, onToggle]);

    // Section location
    useEffect(() => {
        const pathParts = location.pathname.split('/');
        setSectionLocation({
            app: pathParts[1] ?? '',
            subApp: pathParts[2] ?? '',
            section: titleKey ?? '',
        });
    }, [location, titleKey]);

    function toggle() {
        setOpened(!opened);
        if (onToggle) onToggle(!opened);
    }

    return (
        <Card
            {...otherProps}
            className={
                'section-card' + (opened ? '' : ' section-card-collapsed') + (otherProps.className ? ' ' + otherProps.className : '')
            }>
            <CardContent>
                <div className={classes.headerContainer}>
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Icon className={`${classes.icon} d-none d-sm-block`} />
                        <div
                            className={classes.themeSquare}
                            style={{
                                backgroundColor: theme.palette.secondary.main,
                            }}></div>
                        <div className={classes.titleContainer + ' noselect'} onClick={toggle}>
                            <div className={classes.title}>{title}</div>
                        </div>
                    </div>
                    {sectionLocation.section && opened && enableHelp && (
                        <div>
                            <HelperMenu
                                appName={sectionLocation.app}
                                subAppName={sectionLocation.subApp}
                                isManager={team?.isManager ?? true}
                            />
                        </div>
                    )}
                    {!isStatic && (
                        <div className={classes.toggle}>
                            {!opened &&
                                (customSummary ? (
                                    customSummary
                                ) : isCompleted ? (
                                    <CheckCircle className={classes.status} style={{ color: theme.palette.success.main }} />
                                ) : (
                                    <WarningOutlined className={classes.status} style={{ color: theme.palette.error.main }} />
                                ))}
                            <Button onClick={toggle} className={classes.toggleButton}>
                                {opened ? (
                                    <ExpandLessOutlined style={{ color: 'white' }} />
                                ) : (
                                    <ExpandMoreOutlined style={{ color: 'white' }} />
                                )}
                            </Button>
                        </div>
                    )}
                </div>
                {description ? (
                    isDescriptionHtml ? (
                        <Grid container>
                            <Grid item xs={12}>
                                <div className={classes.subtitle} dangerouslySetInnerHTML={{ __html: description }}></div>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container>
                            <Grid item xs={12}>
                                <p className={classes.subtitle}>{description}</p>
                            </Grid>
                        </Grid>
                    )
                ) : null}
                {children}
            </CardContent>
        </Card>
    );
};

export default Section;
